import { EditOutlined } from "@mui/icons-material";
import { useCallback, useId, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import LoanDrilldownChangesForm from "../../../forms/LoanDrilldownChangesForm";
import BasicModal from "../../modals/BasicModal";
import QuickEditDialog from "../../forms/QuickEditDialog";
import RffFieldComponentRenderer from "../../finalForm/shared/RffFieldComponentRenderer";
import RffGroup from "../../finalForm/shared/RffGroup";
import { addressLabel } from "../../../_src_shared/utils/functions/addressLabel";

/**
 * Update root fields on the loan (or a borrower) from a modal.
 *
 * ONE field and no extra body → the quick-edit shell (FORM_STYLE_GUIDE.md
 * §12c): 460px, the field's own label as the title, Cancel + Save. That is
 * what the drilldown header's Expected Closing / Expected Settlement pencils
 * open, and any other single-field caller. More than one field keeps the full
 * modal — there the title can't name them all.
 *
 * Either way the save path is the same `LoanDrilldownChangesForm`; the quick
 * shell just renders the footer itself and submits the form by id.
 */
const UpdateLoanRootDataFieldsModal = ({
  onClose,
  loanId,
  borrowerId,
  propertyId,
  show,
  formSpy,
  property,
  children,
  fields,
  onSuccessFn,
  maxWidth = "xs",
}) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  // One field and no extra body → the quick-edit shell. `children` means the
  // caller is adding content the title can't speak for, so it keeps the full
  // modal even at one field.
  const quick = !children && Array.isArray(fields) && fields.length === 1;
  const formId = `update-loan-field-${useId().replace(/:/g, "")}`;
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleOnClose = useCallback((d) => {
    onClose(false);
    // if (isFunction(onSuccessFn)) {
    //   onSuccessFn(d);
    // }
  }, []);

  const initialValues = useMemo(() => {
    if (borrowerId) {
      return (
        loanDrilldown?.borrowers?.find(
          (borrower) => borrower._id === borrowerId
        ) || {}
      );
    }
    return loanDrilldown;
  }, [loanDrilldown, borrowerId, propertyId]);

  const form = (
    <LoanDrilldownChangesForm
      submitText="Update Loan"
      formSpy={formSpy}
      showFooter={!quick}
      formId={quick ? formId : undefined}
      onHasChangesChange={quick ? setHasChanges : undefined}
      onLoadingChange={quick ? setSaving : undefined}
      onSuccessFn={handleOnClose}
      data={initialValues}
      borrowerId={borrowerId}
      propertyId={propertyId}
    >
      {/* The fields always sit inside a section. In the quick shell that
          section drops the grid (and each field `suppressGrid`s): a spaced MUI
          Grid container is wider than its parent, which a 460px modal can't
          absorb. */}
      {quick ? (
        <RffGroup disableGrid suppressBottomMargin>
          {fields.map((field) => (
            <RffFieldComponentRenderer
              key={field.name}
              field={{ ...field, suppressGrid: true }}
            />
          ))}
        </RffGroup>
      ) : (
        <RffGroup>
          {fields?.map((field) => (
            <RffFieldComponentRenderer key={field.name} field={field} />
          ))}
        </RffGroup>
      )}
    </LoanDrilldownChangesForm>
  );

  if (quick) {
    return (
      <QuickEditDialog
        open={Boolean(show)}
        // The field's label is hidden in this shell, so the title is it.
        title={fields[0]?.label}
        description={addressLabel(property?.address) || undefined}
        onClose={handleOnClose}
        formId={formId}
        saving={saving}
        canSave={hasChanges}
        // Most of these are dates and selects, which take Enter themselves.
        enterHint={false}
      >
        {form}
      </QuickEditDialog>
    );
  }

  return (
    <BasicModal
      title="Update Loan Fields"
      description={addressLabel(property?.address)}
      show={show}
      icon={<EditOutlined className="thin" />}
      onClose={handleOnClose}
      bodySx={{ p: 3 }}
      maxWidth={maxWidth}
    >
      {form}
    </BasicModal>
  );
};

export default UpdateLoanRootDataFieldsModal;
